import React, { createContext, useContext, useMemo, useState, useCallback, useEffect } from 'react';
import { resolveTileType, resolveDetailType, TILE_SIZE, TILE_TYPES, DETAIL_TYPES, TILE_KIND_BASE, TILE_KIND_DETAIL } from './tiles';
import { normalizeTileData } from './tileUtils';
import useImageCache from '../../hooks/useImageCache';
import GameAPI from '../Game/GameAPI';
import { MEDIA_VARIANTS } from '../../media';

const GameWorldContext = createContext(null);

function normalizeMap(map) {
  const tiles = Array.isArray(map?.tiles)
    ? map.tiles
    : (Array.isArray(map?.Tiles) ? map.Tiles : []);
  return {
    width: map?.width || map?.Width || 0,
    height: map?.height || map?.Height || 0,
    campEntranceX: map?.campEntranceX ?? map?.CampEntranceX ?? null,
    campEntranceY: map?.campEntranceY ?? map?.CampEntranceY ?? null,
    exits: Array.isArray(map?.exits)
      ? map.exits
      : (Array.isArray(map?.Exits) ? map.Exits : []),
    tiles,
  };
}

function clamp(value, min, max) {
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

export function GameWorldProvider({
  map,
  initialPlayerPosition,
  movementPath = [],
  movementTarget = null,
  onSelectMovementTarget = null,
  movementPending = false,
  tileset = [],
  decorations = [],
  avatarConfig = null,
  avatarSelection = null,
  campEntrance = null,
  regionExits = [],
  onActivateCampEntrance = null,
  onActivateRegionExit = null,
  travelAllowed = true,
  travelUnavailableReason = '',
  playerAction = 'IDLE',
  playerLevel = 1,
  scavengeRadius = 0,
  regionWorldState = null,
  starterCache = null,
  draggedItem = null,
  onDropInventoryItem = null,
  onOpenGroundLoot = null,
  onOpenWorldLoot = null,
  onOpenStarterCache = null,
  onActivateChampion = null,
  championBusyPlacementId = '',
  queuedWorldIntent = null,
  onRequestWorldIntent = null,
  onConsumeWorldIntent = null,
  onClearWorldIntent = null,
  children,
}) {
  const normalizedMap = useMemo(() => normalizeMap(map), [map]);
  const fetchTileImage = useCallback((imageId, variant) => GameAPI.fetchImage(imageId, variant), []);
  const {
    getImageUrl,
    preload: preloadMapImages,
    revision: mapImageRevision,
  } = useImageCache(fetchTileImage, { batch: true });
  const tileDefinitions = useMemo(() => {
    const definitions = {};

    const register = (tile, fallbackType = TILE_KIND_BASE) => {
      const normalized = normalizeTileData(tile, fallbackType);
      if (!normalized || normalized.type !== TILE_KIND_BASE) return;
      definitions[normalized.id] = normalized;
      definitions[normalized.key] = normalized;
    };

    Object.values(TILE_TYPES).forEach((tile) => register(tile, TILE_KIND_BASE));

    if (Array.isArray(tileset)) {
      tileset.forEach((tile) => register(tile, TILE_KIND_BASE));
    }

    return definitions;
  }, [tileset]);

  const decorationDefinitions = useMemo(() => {
    const definitions = {};

    const register = (tile, fallbackType = TILE_KIND_DETAIL) => {
      const normalized = normalizeTileData(tile, fallbackType);
      if (!normalized || normalized.type !== TILE_KIND_DETAIL) return;
      definitions[normalized.id] = normalized;
      definitions[normalized.key] = normalized;
    };

    Object.values(DETAIL_TYPES).forEach((detail) => register(detail, TILE_KIND_DETAIL));

    if (Array.isArray(decorations)) {
      decorations.forEach((detail) => register(detail, TILE_KIND_DETAIL));
    }

    return definitions;
  }, [decorations]);

  const fallbackBaseTile = useMemo(
    () => tileDefinitions[TILE_TYPES.sand.id] || normalizeTileData(TILE_TYPES.sand, TILE_KIND_BASE),
    [tileDefinitions],
  );

  const [playerPosition, setPlayerPosition] = useState(() => {
    if (initialPlayerPosition) {
      return {
        x: clamp(initialPlayerPosition.x, 0, Math.max(normalizedMap.width - 1, 0)),
        y: clamp(initialPlayerPosition.y, 0, Math.max(normalizedMap.height - 1, 0)),
      };
    }
    return { x: 0, y: 0 };
  });

  useEffect(() => {
    if (!initialPlayerPosition) return;
    const cx = clamp(initialPlayerPosition.x, 0, Math.max(normalizedMap.width - 1, 0));
    const cy = clamp(initialPlayerPosition.y, 0, Math.max(normalizedMap.height - 1, 0));
    setPlayerPosition(current => (
      current.x === cx && current.y === cy
        ? current
        : { x: cx, y: cy }
    ));
  }, [initialPlayerPosition, normalizedMap.width, normalizedMap.height]);

  const getTile = useCallback(
    (x, y) => {
      if (!normalizedMap.tiles[y] || !normalizedMap.tiles[y][x]) return null;
      const tile = normalizedMap.tiles[y][x];
      const baseKey = tile?.base ?? tile?.Base;
      const detailKey = tile?.detail ?? tile?.Detail;
      const walkableOverride = tile?.walkableOverride
        ?? tile?.WalkableOverride
        ?? null;

      let resolvedBase = baseKey ? tileDefinitions[baseKey] : null;
      if (!resolvedBase) {
        const fallback = normalizeTileData(resolveTileType(baseKey), TILE_KIND_BASE);
        if (fallback && fallback.type === TILE_KIND_BASE) {
          resolvedBase = fallback;
        }
      }

      if (!resolvedBase) {
        resolvedBase = fallbackBaseTile;
      }

      let resolvedDetail = null;
      if (detailKey) {
        resolvedDetail = decorationDefinitions[detailKey] || null;
        if (!resolvedDetail) {
          const fallbackDetail = normalizeTileData(resolveDetailType(detailKey), TILE_KIND_DETAIL);
          if (fallbackDetail && fallbackDetail.type === TILE_KIND_DETAIL) {
            resolvedDetail = fallbackDetail;
          }
        }
      }

      const baseImageUrl = resolvedBase?.imageId ? getImageUrl(resolvedBase.imageId, MEDIA_VARIANTS.TILE_TEXTURE) : null;
      const detailImageUrl = resolvedDetail?.imageId ? getImageUrl(resolvedDetail.imageId, MEDIA_VARIANTS.TILE_DETAIL) : null;

      const base = resolvedBase ? {
        ...resolvedBase,
        imageUrl: baseImageUrl,
        walkable: walkableOverride ?? resolvedBase.walkable,
      } : null;
      const detail = resolvedDetail ? { ...resolvedDetail, imageUrl: detailImageUrl } : null;

      return {
        base,
        detail,
      };
    },
    [normalizedMap.tiles, tileDefinitions, decorationDefinitions, fallbackBaseTile, getImageUrl],
  );

  const isWalkable = useCallback(
    (x, y) => {
      if (x < 0 || y < 0 || x >= normalizedMap.width || y >= normalizedMap.height) {
        return false;
      }
      const tile = getTile(x, y);
      return tile ? tile.base.walkable : false;
    },
    [normalizedMap.height, normalizedMap.width, getTile],
  );

  useEffect(() => {
    const images = [];
    normalizedMap.tiles.forEach((row, y) => row.forEach((_, x) => {
      const tile = getTile(x, y);
      if (tile?.base?.imageId) {
        images.push({ imageId: tile.base.imageId, variant: MEDIA_VARIANTS.TILE_TEXTURE });
      }
      if (tile?.detail?.imageId) {
        images.push({ imageId: tile.detail.imageId, variant: MEDIA_VARIANTS.TILE_DETAIL });
      }
    }));
    preloadMapImages(images);
  }, [getTile, normalizedMap.tiles, preloadMapImages]);

  const updatePlayerPosition = useCallback(
    (next) => {
      if (!next) return;
      const cx = clamp(next.x, 0, Math.max(normalizedMap.width - 1, 0));
      const cy = clamp(next.y, 0, Math.max(normalizedMap.height - 1, 0));
      setPlayerPosition(current => (
        current.x === cx && current.y === cy
          ? current
          : { x: cx, y: cy }
      ));
    },
    [normalizedMap.width, normalizedMap.height],
  );

  const contextValue = useMemo(
    () => ({
      map: normalizedMap,
      tileSize: TILE_SIZE,
      playerPosition,
      setPlayerPosition: updatePlayerPosition,
      getTile,
      mapImageRevision,
      isWalkable,
      tileDefinitions,
      decorationDefinitions,
      movementPath,
      movementTarget,
      onSelectMovementTarget,
      movementPending,
      avatarConfig,
      avatarSelection,
      campEntrance,
      regionExits,
      onActivateCampEntrance,
      onActivateRegionExit,
      travelAllowed,
      travelUnavailableReason,
      playerAction,
      playerLevel,
      scavengeRadius,
      regionWorldState,
      starterCache,
      draggedItem,
      onDropInventoryItem,
      onOpenGroundLoot,
      onOpenWorldLoot,
      onOpenStarterCache,
      onActivateChampion,
      championBusyPlacementId,
      queuedWorldIntent,
      onRequestWorldIntent,
      onConsumeWorldIntent,
      onClearWorldIntent,
      clamp: (x, y) => ({
        x: clamp(x, 0, Math.max(normalizedMap.width - 1, 0)),
        y: clamp(y, 0, Math.max(normalizedMap.height - 1, 0)),
      }),
    }),
    [normalizedMap, playerPosition, updatePlayerPosition, getTile, mapImageRevision, isWalkable, tileDefinitions, decorationDefinitions, movementPath, movementTarget, onSelectMovementTarget, movementPending, avatarConfig, avatarSelection, campEntrance, regionExits, onActivateCampEntrance, onActivateRegionExit, travelAllowed, travelUnavailableReason, playerAction, playerLevel, scavengeRadius, regionWorldState, starterCache, draggedItem, onDropInventoryItem, onOpenGroundLoot, onOpenWorldLoot, onOpenStarterCache, onActivateChampion, championBusyPlacementId, queuedWorldIntent, onRequestWorldIntent, onConsumeWorldIntent, onClearWorldIntent],
  );

  return <GameWorldContext.Provider value={contextValue}>{children}</GameWorldContext.Provider>;
}

export function useGameWorld() {
  const ctx = useContext(GameWorldContext);
  if (!ctx) {
    throw new Error('useGameWorld must be used inside a GameWorldProvider');
  }
  return ctx;
}

export function useTileHelpers() {
  const {
    map,
    tileSize,
    playerPosition,
    setPlayerPosition,
    getTile,
    isWalkable,
    clamp: clampPoint,
    tileDefinitions,
    decorationDefinitions,
    movementPath,
    movementTarget,
    onSelectMovementTarget,
    movementPending,
    avatarConfig,
    avatarSelection,
    campEntrance,
    regionExits,
    onActivateCampEntrance,
    onActivateRegionExit,
    travelAllowed,
    travelUnavailableReason,
    playerAction,
    playerLevel,
    scavengeRadius,
    regionWorldState,
    starterCache,
    draggedItem,
    onDropInventoryItem,
    onOpenGroundLoot,
    onOpenWorldLoot,
    onOpenStarterCache,
    onActivateChampion,
    championBusyPlacementId,
    queuedWorldIntent,
    onRequestWorldIntent,
    onConsumeWorldIntent,
    onClearWorldIntent,
  } = useGameWorld();

  const indexFromPoint = useCallback((x, y) => y * map.width + x, [map.width]);
  const pointFromIndex = useCallback(
    (index) => {
      if (!Number.isFinite(index) || index < 0) {
        return { x: 0, y: 0 };
      }
      return {
        x: index % map.width,
        y: Math.floor(index / map.width),
      };
    },
    [map.width],
  );

  return {
    map,
    tileSize,
    playerPosition,
    setPlayerPosition,
    getTile,
    isWalkable,
    clampPoint,
    indexFromPoint,
    pointFromIndex,
    tileDefinitions,
    decorationDefinitions,
    movementPath,
    movementTarget,
    onSelectMovementTarget,
    movementPending,
    avatarConfig,
    avatarSelection,
    campEntrance,
    regionExits,
    onActivateCampEntrance,
    onActivateRegionExit,
    travelAllowed,
    travelUnavailableReason,
    playerAction,
    playerLevel,
    scavengeRadius,
    regionWorldState,
    starterCache,
    draggedItem,
    onDropInventoryItem,
    onOpenGroundLoot,
    onOpenWorldLoot,
    onOpenStarterCache,
    onActivateChampion,
    championBusyPlacementId,
    queuedWorldIntent,
    onRequestWorldIntent,
    onConsumeWorldIntent,
    onClearWorldIntent,
  };
}
